import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import ServerDataTable from '../components/ServerDataTable/ServerDataTable';
import { formatDate } from '../utils/formatDate';
import UserTransferDialog from '../components/UserTransferDialog';
import StatusBadge from '../components/StatusBadge';

const userSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .when('id', {
      is: (id) => !id,
      then: (schema) => schema.required('Password is required'),
      otherwise: (schema) => schema
    }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .when('password', {
      is: (password) => !!password,
      then: (schema) => schema.required('Please confirm your password'),
      otherwise: (schema) => schema
    }),
  role: Yup.string().oneOf(['admin', 'user']).required('Role is required')
});

export default function Users() {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userReferences, setUserReferences] = useState(null);
  const [loadingRows, setLoadingRows] = useState([]);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [originalFormValues, setOriginalFormValues] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');

    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPage = parseInt(pageParam, 10) - 1;
      setPageIndex(newPage);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }
  }, [location.search]);

  // Function to update URL with filter and pagination parameters
  const updateURLWithPagination = (currentPage = pageIndex, currentPageSize = pageSize) => {
    const params = new URLSearchParams();

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || 10);

    // Update URL without reloading the page
    const newURL = params.toString() ? `?${params.toString()}` : '';
    navigate(`/users${newURL}`, { replace: true });
  };

  const formatName = (firstName, lastName) => {
    return `${firstName} ${lastName}`;
  };

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive, email }) => {
      const response = await api.put(`/users/${id}`, { isActive, email });
      return response.data;
    },
    onMutate: async ({ id, isActive }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['users'] });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData(['users', pageIndex, pageSize]);

      // Optimistically update to the new value
      queryClient.setQueryData(['users', pageIndex, pageSize], (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((user) => {
            if (user.id === id) {
              return { ...user, is_active: isActive };
            }
            return user;
          })
        };
      });

      return { previousUsers };
    },
    onSuccess: (data, variables) => {
      const status = variables.isActive ? 'activated' : 'deactivated';
      toast.success(`User ${status} successfully`);
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUsers) {
        queryClient.setQueryData(['users', pageIndex, pageSize], context.previousUsers);
      }
      console.error('Toggle user status error:', err);
      toast.error(
        `Failed to update user status: ${err.response?.data?.message || 'Unknown error'}`
      );
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  const handleToggleActive = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot disable your own account');
      return;
    }

    toggleActiveMutation.mutate({
      id: user.id,
      isActive: !user.is_active,
      email: user.email
    });
  };

  // Columns
  const columns = [
    {
      field: 'first_name',
      headerName: 'Name',
      width: 150,
      sortable: true,
      align: 'left',
      sortField: 'first_name', // Map to database field name
      renderCell: ({ value, row }) => formatName(value, row.last_name)
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      sortable: true,
      sortField: 'email' // Map to database field name
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      sortable: true,
      sortField: 'role', // Map to database field name
      renderCell: ({ value }) => (
        <StatusBadge customLabel={value === 'admin' ? 'Admin' : 'User'} customColor={value === 'admin' ? 'primary' : 'info'} customize={true} />
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 150,
      sortable: true,
      align: 'left',
      sortField: 'is_active', // Map to database field name
      renderCell: ({ row }) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          {row.id === currentUser?.id ? (
            <Tooltip title='You cannot disable your own account'>
              <span>
                <Switch
                  checked={Boolean(row.is_active)}
                  onChange={() =>
                    handleToggleActive(row)
                  }
                  color='primary'
                  disabled={true}
                />
              </span>
            </Tooltip>
          ) : (
            <Switch
              checked={Boolean(row.is_active)}
              onChange={() => handleToggleActive(row)}
              color='primary'
              disabled={
                checkReferencesMutation.isPending ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            />
          )}
          {row.id === currentUser?.id && (
            <Tooltip title='This is your account'>
              <InfoIcon
                fontSize='small'
                color='info'
                sx={{ ml: 1 }}
              />
            </Tooltip>
          )}
        </Box>
      )
    },
    {
      field: 'last_login',
      headerName: 'Last Login',
      width: 200,
      sortable: true,
      sortField: 'last_login', // Map to database field name
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      align: 'right',
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size='small' onClick={() => handleEdit(row)}>
              <EditIcon />
            </IconButton>
            {row.id !== currentUser?.id ? (
              <IconButton size='small' color='error' onClick={() => handleDelete(row)}>
                <DeleteIcon />
              </IconButton>
            ) : (
              <IconButton size='small' color='error' disabled>
                <DeleteIcon disabled />
              </IconButton>
            )}
          </Box>
        );
      }
    }
  ];

  // Queries

  const {
    data: users = { data: [], pagination: { total: 0, totalPages: 0 } },
    isLoading: isLoadingUsers
  } = useQuery({
    queryKey: ['users', pageIndex, pageSize],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          page: pageIndex + 1,
          pageSize: pageSize.toString()
        });

        const response = await api.get(`/users?${params.toString()}`);

        // Handle the standard API response format: { status: "success", data: users, pagination: {...} }
        if (response.data && response.data.status === 'success') {
          return {
            data: response.data.data || [],
            pagination: response.data.pagination || { total: 0, totalPages: 0 }
          };
        }

        // Fallback for direct data response
        else if (Array.isArray(response.data)) {
          return {
            data: response.data,
            pagination: { total: response.data.length, totalPages: 1 }
          };
        }

        return { data: [], pagination: { total: 0, totalPages: 0 } };
      } catch (error) {
        console.error('Error fetching users:', error);
        return { data: [], pagination: { total: 0, totalPages: 0 } };
      }
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true
  });

  // Mutations

  const createMutation = useMutation({
    mutationFn: async (userData) => {
      const response = await api.post('/users', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsDialogOpen(false);
      formik.resetForm();
      setHasFormChanges(false);
      toast.success('User created successfully');
    },
    onError: (error) => {
      console.error('Create user error:', error);
      toast.error(
        `Failed to create user: ${error.response?.data?.message || 'Unknown error'
        }`
      );
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (userData) => {
      const { id, ...data } = userData;
      if (!data.password) {
        delete data.password;
      }
      delete data.confirmPassword;

      const response = await api.put(`/users/${id}`, data);
      return {
        response: response.data,
        passwordChanged: !!data.password,
        userId: id
      };
    },
    onMutate: async (userData) => {
      setLoadingRows(prev => [...prev, userData.id]);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsDialogOpen(false);
      formik.resetForm();
      setHasFormChanges(false);
      setLoadingRows(prev => prev.filter(id => id !== data.userId));

      if (data.passwordChanged && data.userId === currentUser?.id) {
        toast.success('Password changed successfully. You will be logged out.');
        setTimeout(() => {
          logout();
        }, 1500);
      } else {
        toast.success('User updated successfully');
      }
    },
    onError: (error, userData) => {
      console.error('Update user error:', error);
      setLoadingRows(prev => prev.filter(id => id !== userData.id));
      toast.error(
        `Failed to update user: ${error.response?.data?.message || 'Unknown error'
        }`
      );
    }
  });

  // New mutation for checking user references
  const checkReferencesMutation = useMutation({
    mutationFn: async (userId) => {
      try {
        const response = await api.delete(`/users/${userId}`);
        return response.data;
      } catch (error) {
        if (error.response?.status === 409) {
          // User has references, return the conflict data
          return error.response.data;
        }
        throw error;
      }
    },
    onMutate: async (userId) => {
      setLoadingRows(prev => [...prev, userId]);
    },
    onSuccess: (data, userId) => {
      if (data.status === 'conflict') {
        // User has references, show transfer dialog
        setUserReferences(data.data.references);
        setSelectedUser(data.data.user);
        setIsDeleteDialogOpen(false);
        setIsTransferDialogOpen(true);
      } else {
        // No references, user was already deleted successfully
        queryClient.invalidateQueries({ queryKey: ['users'] });
        setIsDeleteDialogOpen(false);
        setSelectedUser(null);
        setLoadingRows(prev => prev.filter(loadingId => loadingId !== userId));
        toast.success('User deleted successfully');
      }
    },
    onError: (error, userId) => {
      console.error('Check references error:', error);
      setLoadingRows(prev => prev.filter(loadingId => loadingId !== userId));
      toast.error(
        `Failed to check user references: ${error.response?.data?.message || 'Unknown error'}`
      );
    }
  });

  const formik = useFormik({
    initialValues: {
      id: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      role: 'user'
    },
    validationSchema: userSchema,
    onSubmit: (values) => {
      if (values.id) {
        updateMutation.mutate(values);
      } else {
        createMutation.mutate(values);
      }
    }
  });

  // Track changes compared to original form values
  useEffect(() => {
    if (Object.keys(originalFormValues).length > 0 && formik.values) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(originalFormValues);
      setHasFormChanges(hasChanges);
    }
  }, [formik.values, originalFormValues]);

  // Handles

  const handleDialogClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      setIsDialogOpen(false);
      setShowPassword(false);
      formik.resetForm();
      setHasFormChanges(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithPagination(newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithPagination(0, newPageSize);
  };

  const handleEdit = (user) => {
    if (!updateMutation.isPending && !createMutation.isPending) {
      setSelectedUser(user);
      const formValues = {
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        password: '',
        confirmPassword: '',
        role: user.role
      };
      formik.setValues(formValues);
      setOriginalFormValues(JSON.parse(JSON.stringify(formValues)));
      setHasFormChanges(false);
      setIsDialogOpen(true);
    }
  };

  const handleAdd = () => {
    if (!updateMutation.isPending && !createMutation.isPending) {
      setSelectedUser(null);
      const formValues = {
        id: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        role: 'user'
      };
      formik.resetForm({ values: formValues });
      setOriginalFormValues(JSON.parse(JSON.stringify(formValues)));
      setHasFormChanges(false);
      setIsDialogOpen(true);
    }
  };

  const handleDelete = (user) => {
    if (user.id === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!checkReferencesMutation.isPending) {
      setSelectedUser(user);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUser && !checkReferencesMutation.isPending) {
      checkReferencesMutation.mutate(selectedUser.id);
    }
  };

  const handleTransferSuccess = () => {
    setUserReferences(null);
    setSelectedUser(null);
    setIsTransferDialogOpen(false);
    // Clear loading state for the user that was being processed
    if (selectedUser?.id) {
      setLoadingRows(prev => prev.filter(loadingId => loadingId !== selectedUser.id));
    }
  };

  const handleTransferDialogClose = () => {
    if (!isTransferDialogOpen) return;
    setIsTransferDialogOpen(false);
    setUserReferences(null);
    setSelectedUser(null);
    // Clear loading state for the user that was being processed
    if (selectedUser?.id) {
      setLoadingRows(prev => prev.filter(loadingId => loadingId !== selectedUser.id));
    }
  };

  return (
    <Box>
      <PageHeader
        title='User Management'
        subtitle='Manage users and their access to the system'
        button={{
          text: 'Add New',
          onClick: handleAdd,
          icon: <AddIcon />
        }}
      />

      <ServerDataTable
        data={users?.data || []}
        columns={columns}
        totalRows={users?.pagination?.total || 0}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoadingUsers}
        loadingRows={loadingRows}
        noDataMessage='No users found.'
        totalPages={users?.pagination?.totalPages || 0}
      />

      {/* Add/Edit User Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ py: 2, px: 3, fontSize: 18 }}>
          {selectedUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={isMobile ? 1 : 3}>
            {createMutation.isPending || updateMutation.isPending ? (
              <>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton
                    variant='rectangular'
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='firstName'
                    name='firstName'
                    label='First Name'
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.firstName &&
                      Boolean(formik.errors.firstName)
                    }
                    helperText={
                      formik.touched.firstName && formik.errors.firstName
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id='lastName'
                    name='lastName'
                    label='Last Name'
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.lastName &&
                      Boolean(formik.errors.lastName)
                    }
                    helperText={
                      formik.touched.lastName && formik.errors.lastName
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id='email'
                    name='email'
                    label='Email Address'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.email && Boolean(formik.errors.email)
                    }
                    helperText={formik.touched.email && formik.errors.email}
                    autoComplete='new-password'
                    inputProps={{
                      autoComplete: 'new-password' // inputProps autocomplete
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id='password'
                    name='password'
                    label={
                      selectedUser
                        ? 'Password (leave blank to keep current)'
                        : 'Password'
                    }
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password &&
                      Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    autoComplete='new-password'
                    inputProps={{
                      autoComplete: 'new-password' // inputProps autocomplete
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label='toggle password visibility'
                          onClick={() => setShowPassword(!showPassword)}
                          edge='end'
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id='confirmPassword'
                    name='confirmPassword'
                    label='Confirm Password'
                    type={showPassword ? 'text' : 'password'}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                    }
                    autoComplete='new-password'
                    inputProps={{
                      autoComplete: 'new-password' // inputProps autocomplete
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    id='role'
                    name='role'
                    label='Role'
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.role && Boolean(formik.errors.role)}
                    helperText={formik.touched.role && formik.errors.role}
                  >
                    <MenuItem value='user'>User</MenuItem>
                    <MenuItem value='admin'>Admin</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant='contained'
            color='secondary'
            onClick={handleDialogClose}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={
              !formik.isValid ||
              !hasFormChanges ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            onClick={() => {
              if (formik.isValid && hasFormChanges) {
                if (selectedUser) {
                  updateMutation.mutate(formik.values);
                } else {
                  createMutation.mutate(formik.values);
                }
              }
            }}
          >

            {createMutation.isPending || updateMutation.isPending ? (
              selectedUser ? 'Updating...' : 'Creating...'
            ) : selectedUser ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={isDeleteDialogOpen}
        title='Delete User'
        message={`Are you sure you want to delete the user ${selectedUser?.first_name} ${selectedUser?.last_name} (${selectedUser?.email})? This action cannot be undone.`}
        confirmText={
          checkReferencesMutation.isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Deleting...
            </Box>
          ) : (
            'Delete'
          )
        }
        confirmColor='error'
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!checkReferencesMutation.isPending) {
            setIsDeleteDialogOpen(false);
            setSelectedUser(null);
          }
        }}
        isLoading={checkReferencesMutation.isPending}
      />

      {/* User Transfer Dialog */}
      <UserTransferDialog
        open={isTransferDialogOpen}
        onClose={handleTransferDialogClose}
        userToDelete={selectedUser}
        references={userReferences}
        onSuccess={handleTransferSuccess}
      />
    </Box>
  );
}
